import { useCallback, useEffect, useMemo, useState } from "react";
import "../../css/CreateEvent.css";
import {
  FormCheckBoxField,
  FormInput,
  FormInputType,
  FormRadioField,
  NotificationType,
  SavedFormQuestion,
  ThemeType,
} from "@markit/common.types";
import { DarkTheme, LightTheme, ThemeStyle } from "../../hooks/useTheme";
import { Colors } from "../../utils/colors";
import { Icon } from "@iconify/react";
import SwitchToggleItem from "../SwitchToggleItem";
import { Checkbox } from "@mui/material";
import { generate } from "shortid";
import checkbox from "@iconify/icons-mdi/checkbox-outline";
import PopupModalContainer from "../Containers/PopupModalContainer";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "../../redux/store";
import RectangleButton from "../Buttons/RectangleButton";
import {
  isOriginalOptionOccurrence,
  originalOptionCounts,
  questionInputName,
} from "../../utils/eventUtils/formUtils";
import {
  LoginState,
  accountActions,
  createSingleSavedQuestion,
  editSingleProfileQuestion,
  updateSavedQuestion,
} from "../../redux/slices/accountSlice";
import { Batch, getUserSavedFormQuestionRef } from "../../utils/FirebaseUtils";
import AlertContainer from "../Containers/AlertContainer";
import ConfirmActionModal from "../Containers/ConfirmPopups/ConfirmActionModal";
import { FormSavedQuestionsType } from "./FormSavedQuestions";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import { DropdownMenu, DropdownMenuItem } from "../DropdownMenu";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import CustomTextField from "../CustomTextField";
import CustomTextFieldMultiline from "../TextFields/CustomTextFieldMultiline";
import { StandardSectionContainer } from "../Containers/StandardSectionContainer";
import { showNotificationBanner } from "../../utils/notificationUtils";
import { ContainerButton } from "../Buttons/ContainerButton";
import CustomCheckbox from "../CustomCheckbox";
import DragDropList from "../Lists/DragDropList";
import {
  formQuestionDisplayDescription,
  generateNewSavedFormQuestion,
} from "@markit/common.utils";
import DragDropListIcon from "../Lists/DragDropListIcon";
import MediaUploadFile from "../Media/MediaUploadFile";
import DropdownArrowIcon from "../DropdownsAndTabs/DropdownArrowIcon";

interface FormBuilderProps {
  type: FormSavedQuestionsType;
  selectedQuestions: SavedFormQuestion[];
  setSelectedQuestions: (eventFormQuestions: SavedFormQuestion[]) => void;
  setIsFormsModalOpen: (formsModalOpen: boolean) => void;
  formQuestionItem?: SavedFormQuestion | undefined;
  setFormQuestionItem?: (
    newFormQuestion: SavedFormQuestion | undefined
  ) => void;
  newQuestionOptions?: string[];
  theme?: ThemeStyle;
}

const FormBuilder = (props: FormBuilderProps) => {
  const {
    type,
    selectedQuestions,
    setSelectedQuestions,
    setIsFormsModalOpen,
    formQuestionItem,
    setFormQuestionItem,
    newQuestionOptions,
    theme,
  } = props;
  const dispatch = useDispatch();
  const { savedQuestions, loggedIn, currentEventFormQuestions, accountData } =
    useSelector((state: AppState) => state.account);
  const [newFormQuestion, setNewFormQuestion] = useState<SavedFormQuestion>(
    generateNewSavedFormQuestion(generate(), FormInputType.TEXT_INPUT)
  ); // The current local form question to manage state when creating/editing
  const [essentialModalVisible, setEssentialModalVisible] = useState(false);
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  const [alertText, setAlertText] = useState("");
  const [confirmEditsModal, setConfirmEditsModal] = useState(false);
  const [changeLabelConfirm, setChangeLabelConfirm] = useState(false);
  const [inactiveAnswersModal, setInactiveAnswersModal] = useState(false);
  const [selectedInactiveAnswers, setSelectedInactiveAnswers] = useState<
    string[]
  >([]);

  const themeType = useMemo(
    () => (!theme || theme === LightTheme ? ThemeType.Light : ThemeType.Dark),
    [theme]
  );

  const essentialQuestion = useMemo(
    () => savedQuestions.find((question) => question.isEssential),
    [savedQuestions]
  );

  const currentFormQuestions = useMemo(
    () =>
      type === FormSavedQuestionsType.PROFILE
        ? savedQuestions.filter((question) => question.onProfileFollow)
        : currentEventFormQuestions,
    [currentEventFormQuestions, savedQuestions, type]
  );

  const questionTypeDisabled = useMemo(
    () =>
      formQuestionItem !== undefined &&
      (formQuestionItem.eventIds.length > 0 ||
        formQuestionItem.onProfileFollow ||
        formQuestionItem.formInput.inputType === FormInputType.EMAIL),
    [formQuestionItem]
  );

  const newInactiveOptions = useMemo(
    () =>
      (newFormQuestion.formInput as FormCheckBoxField | FormRadioField)
        .inactiveOptions,
    [newFormQuestion.formInput]
  );

  const questionTypeDescription = useMemo(
    () => formQuestionDisplayDescription(newFormQuestion.formInput.inputType),
    [newFormQuestion.formInput.inputType]
  );

  const changeIsEssential = useCallback(
    (isEssential: boolean) => {
      const formQuestion: SavedFormQuestion = {
        ...newFormQuestion,
        isEssential: isEssential,
      };
      setNewFormQuestion(formQuestion);
    },
    [newFormQuestion]
  );

  const changeIsRequired = useCallback(() => {
    const updatedFormQuestion: SavedFormQuestion = {
      ...newFormQuestion,
      formInput: {
        ...newFormQuestion.formInput,
        isRequired: !newFormQuestion.formInput.isRequired,
      },
    };
    setNewFormQuestion(updatedFormQuestion);
  }, [newFormQuestion]);

  useEffect(() => {
    // default isEssential to true if the user does not have an essential question yet, and logged in
    if (
      loggedIn === LoginState.LOGGED_IN &&
      !essentialQuestion &&
      !formQuestionItem &&
      !selectedQuestions.some((question) => question.isEssential)
    ) {
      const formQuestion: SavedFormQuestion = {
        ...newFormQuestion,
        isEssential: true,
      };
      setNewFormQuestion(formQuestion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (formQuestionItem) {
      setNewFormQuestion(formQuestionItem);
    }
  }, [formQuestionItem]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean, info?: { source: "trigger" | "menu" }) => {
      if (info?.source === "trigger" || nextOpen) {
        setIsOpenDropdown(nextOpen);
      }
    },
    [setIsOpenDropdown]
  );

  const changeInput = useCallback(
    (newInput: Partial<FormInput>) => {
      const updatedFormQuestion: SavedFormQuestion = {
        ...newFormQuestion,
        formInput: { ...newFormQuestion.formInput, ...newInput } as FormInput,
      };
      setNewFormQuestion(updatedFormQuestion);
    },
    [newFormQuestion, setNewFormQuestion]
  );

  const addInput = useCallback(
    (type: FormInputType) => {
      // Maintain the same id and label on changing input types
      const newQuestion = generateNewSavedFormQuestion(
        newFormQuestion.id,
        type
      );
      const formInput = newQuestionOptions
        ? {
            ...newQuestion.formInput,
            label: newFormQuestion.formInput.label ?? "",
            options:
              type === FormInputType.CHECK_BOX
                ? newQuestionOptions.flatMap((option) => option.split(", "))
                : newQuestionOptions,
          }
        : {
            ...newQuestion.formInput,
            label: newFormQuestion.formInput.label ?? "",
          };
      const newSavedFormQuestion: SavedFormQuestion = {
        ...newQuestion,
        formInput: formInput,
      };
      setNewFormQuestion(newSavedFormQuestion);
    },
    [newFormQuestion.formInput.label, newFormQuestion.id, newQuestionOptions]
  );

  // Move the answer option to inactive if it's an existing option (editing case), otherwise simply remove it
  const deleteInputOption = useCallback(
    (index: number) => {
      const { options, inactiveOptions = [] } = newFormQuestion.formInput as
        | FormCheckBoxField
        | FormRadioField;
      const optionToRemove = options[index];
      const updatedOptions = options.filter((_, i) => i !== index);

      if (formQuestionItem) {
        const {
          options: originalOptions,
          inactiveOptions: originalInactiveOptions,
        } = formQuestionItem.formInput as FormCheckBoxField | FormRadioField;
        const allOriginalOptions = originalOptions.concat(
          originalInactiveOptions
        );

        changeInput({
          options: updatedOptions,
          inactiveOptions: allOriginalOptions.includes(optionToRemove)
            ? [...inactiveOptions, optionToRemove]
            : inactiveOptions,
        });
      } else {
        changeInput({ options: updatedOptions });
      }
    },
    [changeInput, formQuestionItem, newFormQuestion.formInput]
  );

  // Add the selected inactive answers back into the active options
  const activateInputOptions = useCallback(() => {
    if (selectedInactiveAnswers.length === 0) {
      setAlertText("Please select at least one inactive answer.");
      return;
    }
    const { options, inactiveOptions } = newFormQuestion.formInput as
      | FormCheckBoxField
      | FormRadioField;
    changeInput({
      options: [...options, ...selectedInactiveAnswers],
      inactiveOptions: inactiveOptions.filter(
        (option) => !selectedInactiveAnswers.includes(option)
      ),
    });
    setSelectedInactiveAnswers([]);
    setInactiveAnswersModal(false);
  }, [
    changeInput,
    newFormQuestion.formInput,
    selectedInactiveAnswers,
    setSelectedInactiveAnswers,
    setInactiveAnswersModal,
  ]);

  const resetOnPress = useCallback(() => {
    setNewFormQuestion(
      generateNewSavedFormQuestion(generate(), FormInputType.TEXT_INPUT)
    );
    setIsFormsModalOpen(false);
    if (setFormQuestionItem) {
      setFormQuestionItem(undefined);
    }
  }, [setFormQuestionItem, setIsFormsModalOpen]);

  const finalizeFormQuestion = useCallback(() => {
    const finalNewFormQuestion: SavedFormQuestion = {
      ...newFormQuestion,
      formInput: {
        ...newFormQuestion.formInput,
        label: newFormQuestion.formInput.label?.trim() ?? "",
      },
    };
    let updatedFormQuestions: SavedFormQuestion[] = [...selectedQuestions];
    const index = selectedQuestions.findIndex(
      (question) => question.id === finalNewFormQuestion.id
    );
    if (index !== -1) {
      updatedFormQuestions.splice(index, 1, finalNewFormQuestion);
    } else {
      updatedFormQuestions = selectedQuestions.concat(finalNewFormQuestion);
    }

    // remove essential from any selectedQuestions that has it marked as true (local)
    // if the newFormQuestion is also marked as essential
    const foundOldSelectedEssential = selectedQuestions.find(
      (question) => question.isEssential
    );
    if (foundOldSelectedEssential && finalNewFormQuestion.isEssential) {
      const newQuestion: SavedFormQuestion = {
        ...foundOldSelectedEssential,
        isEssential: false,
      };
      const index = selectedQuestions.findIndex(
        (question) => question.isEssential
      );
      if (index !== -1) {
        updatedFormQuestions.splice(index, 1, newQuestion);
      }
    }
    setSelectedQuestions(updatedFormQuestions);

    // remove essential from any currEventFormQuestions that has it marked as true (redux)
    const foundOldCurrEssential = currentFormQuestions.find(
      (question) => question.isEssential
    );
    if (
      type === FormSavedQuestionsType.EVENT &&
      foundOldCurrEssential &&
      finalNewFormQuestion.isEssential
    ) {
      const newQuestion: SavedFormQuestion = {
        ...foundOldCurrEssential,
        isEssential: false,
      };
      const index = currentFormQuestions.findIndex(
        (question) => question.isEssential
      );
      if (index !== -1) {
        dispatch(accountActions.updateCurrentEventFormQuestions([newQuestion]));
      }
    }

    // update any found currEventFormQuestion that exists (editing) in redux
    if (
      currentFormQuestions.some(
        (question) => question.id === finalNewFormQuestion.id
      )
    ) {
      dispatch(
        accountActions.updateCurrentEventFormQuestions([finalNewFormQuestion])
      );
    }

    // update the savedQuestion in redux after editing a profile question
    if (type === FormSavedQuestionsType.PROFILE) {
      dispatch(
        editSingleProfileQuestion(accountData.uid, finalNewFormQuestion)
      );
    } else if (
      type === FormSavedQuestionsType.DATA ||
      type === FormSavedQuestionsType.ASSIGN
    ) {
      // Editing case
      if (formQuestionItem) {
        dispatch(updateSavedQuestion(accountData.uid, finalNewFormQuestion));
      } else {
        dispatch(
          createSingleSavedQuestion(accountData.uid, finalNewFormQuestion)
        );
      }
      showNotificationBanner(
        dispatch,
        formQuestionItem ? "Question Updated" : "Question Created",
        NotificationType.AFFIRMATIVE
      );
    }

    resetOnPress();
  }, [
    accountData.uid,
    currentFormQuestions,
    dispatch,
    formQuestionItem,
    newFormQuestion,
    resetOnPress,
    selectedQuestions,
    setSelectedQuestions,
    type,
  ]);

  const saveNewFormInput = useCallback(() => {
    const formInput = newFormQuestion.formInput;
    const finalLabel = newFormQuestion.formInput.label?.trim();
    const formIsValid = () => {
      // check if label name already exists as another form question
      if (
        savedQuestions.some(
          (question) => question.formInput.label === finalLabel
        ) &&
        !formQuestionItem
      ) {
        setAlertText(
          "You already have a saved form question with the same name."
        );
        return false;
      }
      switch (formInput.inputType) {
        case FormInputType.TEXT_INPUT:
          if (!finalLabel) {
            setAlertText("Short answer questions must have a label.");
            return false;
          }
          return true;
        case FormInputType.RADIO:
        case FormInputType.CHECK_BOX: {
          if (
            formInput.options.some((option) => option === "") ||
            !finalLabel
          ) {
            setAlertText("All answer options and the label must be non-empty.");
            return false;
          } else if (formInput.options.length === 0) {
            setAlertText(
              "Question must have at least one answer option and label."
            );
            return false;
          }
          // Checks if any of the new options match existing options
          const hasNewOptionMatchingExisting = formQuestionItem
            ? formInput.options.some(
                (option, optionIndex) =>
                  (originalOptionCounts(formQuestionItem).get(option) || 0) >
                    0 &&
                  !isOriginalOptionOccurrence(
                    formQuestionItem,
                    formInput.options,
                    option,
                    optionIndex
                  )
              )
            : false;

          if (hasNewOptionMatchingExisting) {
            setAlertText(
              "One or more of your new answer options match existing or inactive options. Please remove before saving."
            );
            return false;
          }
          return true;
        }
        default:
          return true;
      }
    };

    if (formIsValid()) {
      // Check if any existing options were moved to inactiveOptions
      const hasRemovedOptions =
        formQuestionItem &&
        (formQuestionItem.formInput.inputType === FormInputType.RADIO ||
          formQuestionItem.formInput.inputType === FormInputType.CHECK_BOX) &&
        formQuestionItem.formInput.options.some((option) =>
          (
            newFormQuestion.formInput as FormCheckBoxField | FormRadioField
          ).inactiveOptions?.includes(option)
        );

      if (formQuestionItem && finalLabel !== formQuestionItem.formInput.label) {
        setChangeLabelConfirm(true);
      } else if (hasRemovedOptions) {
        setConfirmEditsModal(true);
      } else {
        finalizeFormQuestion();
      }
    }
  }, [
    newFormQuestion.formInput,
    savedQuestions,
    formQuestionItem,
    finalizeFormQuestion,
  ]);

  const handleOnDrag = useCallback(
    (newOptions: string[]) => {
      changeInput({ options: newOptions });
    },
    [changeInput]
  );

  const essentialCheckmarkOnPress = useCallback(() => {
    if (essentialQuestion) {
      setEssentialModalVisible(true);
    } else if (!newFormQuestion.isEssential) {
      changeIsEssential(true);
    } else {
      changeIsEssential(false);
    }
  }, [changeIsEssential, essentialQuestion, newFormQuestion.isEssential]);

  // if changing essential question when you already have an essential question:
  // Change the old saved question to not essential locally and in firebase
  // Change the redux state for the essential saved question
  const changeEssentialQuestionOnPress = useCallback(
    async (newEssentialState: boolean) => {
      if (essentialQuestion) {
        const batch = new Batch("Error updating essential question");
        // update old essential question
        const oldFormQuestion: SavedFormQuestion = {
          ...essentialQuestion,
          isEssential: false,
        };
        if (
          currentFormQuestions.some(
            (question) => question.id === essentialQuestion.id
          )
        ) {
          dispatch(
            accountActions.updateCurrentEventFormQuestions([oldFormQuestion])
          );
        }
        const oldEssentialQuestionRef = getUserSavedFormQuestionRef(
          accountData.uid,
          essentialQuestion.id
        );
        batch.update(oldEssentialQuestionRef, { isEssential: false });

        try {
          await batch.commit();
          // change redux state for essential question
          dispatch(accountActions.addSavedFormQuestions([oldFormQuestion]));
          changeIsEssential(newEssentialState);
        } catch (err: any) {
          setAlertText(
            "Sorry, there was an error switching your essential question at this time."
          );
        }
        setEssentialModalVisible(false);
      }
    },
    [
      accountData.uid,
      changeIsEssential,
      currentFormQuestions,
      dispatch,
      essentialQuestion,
    ]
  );

  // Check if the option should be locked from editing (part of original options)
  const isLockedOriginalAnswerOption = useCallback(
    (optionValue: string, optionIndex: number) => {
      if (!formQuestionItem) {
        return false;
      }
      const currentOptions = (
        newFormQuestion.formInput as FormCheckBoxField | FormRadioField
      ).options;
      return isOriginalOptionOccurrence(
        formQuestionItem,
        currentOptions,
        optionValue,
        optionIndex
      );
    },
    [formQuestionItem, newFormQuestion.formInput]
  );

  const toggleHasOtherOption = useCallback(() => {
    const inputType = newFormQuestion.formInput.inputType;
    if (
      inputType === FormInputType.RADIO ||
      inputType === FormInputType.CHECK_BOX
    ) {
      changeInput({
        hasOtherOption: !newFormQuestion.formInput.hasOtherOption,
      });
    }
  }, [changeInput, newFormQuestion.formInput]);

  const dropdownItems: DropdownMenuItem[] = useMemo(() => {
    return [
      {
        title: "Short Answer",
        key: "short_answer",
        icon: "mdi:text-box",
        onPress: () => {
          addInput(FormInputType.TEXT_INPUT);
          setIsOpenDropdown(false);
        },
      },
      {
        title: "Multiple Choice",
        key: "multiple_choice",
        icon: "mdi:circle-multiple",
        onPress: () => {
          addInput(FormInputType.RADIO);
          setIsOpenDropdown(false);
        },
      },
      {
        title: "Checkbox",
        key: "checkbox",
        icon: "mdi:checkbox-multiple-marked",
        onPress: () => {
          addInput(FormInputType.CHECK_BOX);
          setIsOpenDropdown(false);
        },
      },
      {
        title: "File Upload",
        key: "file_upload",
        icon: "ion:document",
        onPress: () => {
          addInput(FormInputType.FILE_UPLOAD);
          setIsOpenDropdown(false);
        },
      },
    ];
  }, [addInput]);

  const previewLabel = useMemo(
    () => (
      <div className="AlignedRow" style={{ gap: 4 }}>
        <span className="bodyMedium" style={theme?.PrimaryText}>
          Preview
        </span>
        <Icon
          icon="mdi:lock-outline"
          height={16}
          style={{
            color: theme === LightTheme || !theme ? Colors.BLACK : Colors.WHITE,
          }}
        />
      </div>
    ),
    [theme]
  );

  const renderQuestionNameSection = useMemo(
    () => (
      <div className="ColumnNormal" style={{ gap: 7 }}>
        <div className="AlignedRowSpaced">
          <span className="bodyMedium" style={theme?.PrimaryText}>
            Question
          </span>
          <span className="smallBodySubtext">
            {newFormQuestion.formInput.label?.length || 0}/1000
          </span>
        </div>
        <CustomTextField
          value={newFormQuestion.formInput.label || ""}
          placeholder={"Write your question..."}
          inputMode="text"
          borderRadius={12}
          onChange={(e) => {
            changeInput({ label: e.target.value });
          }}
          maxLength={1000}
          multiline
          minRows={1}
          maxRows={3}
          eventTheme={themeType}
        />
      </div>
    ),
    [
      changeInput,
      newFormQuestion.formInput.label,
      theme?.PrimaryText,
      themeType,
    ]
  );

  return (
    <>
      {!inactiveAnswersModal ? (
        <PopupModalContainer
          headerComp={
            formQuestionItem !== undefined ? "Edit Question" : "New Question"
          }
          noExit
          valueComp={
            <div className="ColumnNormal" style={{ gap: 20, paddingBlock: 7 }}>
              <div className="ColumnNormal" style={{ gap: 7 }}>
                <span className="bodyMedium" style={{ ...theme?.PrimaryText }}>
                  Question Type
                </span>
                <div style={{ width: 212 }}>
                  <DropdownMenu
                    dropdownItems={dropdownItems}
                    trigger={["click"]}
                    placement="bottom"
                    isOpen={isOpenDropdown}
                    onOpenChange={handleOpenChange}
                    dark={theme === DarkTheme}
                    disabled={questionTypeDisabled}
                  >
                    <StandardBorderedContainer
                      containerStyles={{
                        backgroundColor: theme
                          ? theme.NewPrimaryBG.backgroundColor
                          : Colors.WHITE1,
                        border: `0.5px solid ${
                          theme?.EditableTextFieldBorder.borderColor ||
                          Colors.GRAY2
                        }`,
                        padding: 14,
                        opacity: questionTypeDisabled ? 0.5 : 1,
                        cursor: questionTypeDisabled
                          ? "not-allowed"
                          : "pointer",
                      }}
                    >
                      <div className="AlignedRow" style={{ gap: 10 }}>
                        <span
                          className="bodyMedium"
                          style={{ ...theme?.PrimaryText }}
                        >
                          {questionInputName(
                            newFormQuestion.formInput.inputType
                          )}
                        </span>
                        {!questionTypeDisabled ? (
                          <DropdownArrowIcon
                            isOpen={isOpenDropdown}
                            height={14}
                            color={theme?.PrimaryText.color || Colors.BLACK}
                          />
                        ) : null}
                      </div>
                    </StandardBorderedContainer>
                  </DropdownMenu>
                </div>
                {questionTypeDescription ? (
                  <span className="smallBodySubtext">
                    {questionTypeDescription}
                  </span>
                ) : null}
              </div>
              {newFormQuestion.formInput.inputType === FormInputType.RADIO ||
              newFormQuestion.formInput.inputType ===
                FormInputType.CHECK_BOX ? (
                <div className="ColumnNormal" style={{ gap: 20 }}>
                  {renderQuestionNameSection}
                  <div className="ColumnNormal" style={{ gap: 7 }}>
                    <span className="bodyMedium" style={theme?.PrimaryText}>
                      Answer Options
                    </span>
                    {newFormQuestion.formInput.options ? (
                      <div className="ColumnNormal" style={{ gap: 7 }}>
                        <DragDropList
                          items={newFormQuestion.formInput.options.map(
                            (option, index) => ({
                              id: `option-${index}`,
                              value: option,
                            })
                          )}
                          handleChange={(newItems) => {
                            const newOptions = newItems.map(
                              (item) => item.value
                            );
                            handleOnDrag(newOptions);
                          }}
                        >
                          {(
                            option: { id: string; value: string },
                            index: number,
                            dragHandleProps: any
                          ) => (
                            <div
                              key={option.id}
                              className="AlignedRow"
                              style={{ gap: 10 }}
                            >
                              <CustomTextField
                                value={option.value}
                                placeholder={"Answer..."}
                                inputMode="text"
                                borderRadius={12}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  const { options, inactiveOptions = [] } =
                                    newFormQuestion.formInput as
                                      | FormCheckBoxField
                                      | FormRadioField;
                                  // If you type an option that exists in inactiveOptions, reactivate it automatically
                                  const reactivated =
                                    inactiveOptions.includes(newValue);
                                  changeInput({
                                    options: options.map((o, i) =>
                                      i === index ? newValue : o
                                    ),
                                    inactiveOptions: inactiveOptions.filter(
                                      (option) => option !== newValue
                                    ),
                                  });
                                  if (reactivated) {
                                    showNotificationBanner(
                                      dispatch,
                                      "Reactivated Inactive Option",
                                      NotificationType.AFFIRMATIVE
                                    );
                                  }
                                }}
                                maxLength={1000}
                                multiline
                                minRows={1}
                                maxRows={3}
                                eventTheme={themeType}
                                disabled={isLockedOriginalAnswerOption(
                                  option.value,
                                  index
                                )}
                              />
                              {(
                                newFormQuestion.formInput as
                                  | FormCheckBoxField
                                  | FormRadioField
                              ).options.length > 1 ? (
                                <div>
                                  <Icon
                                    icon={"ion:trash"}
                                    height={18}
                                    className="TrashButton"
                                    onClick={() => deleteInputOption(index)}
                                  />
                                </div>
                              ) : null}
                              <DragDropListIcon
                                dragHandleProps={dragHandleProps}
                              />
                            </div>
                          )}
                        </DragDropList>
                        {newFormQuestion.formInput.hasOtherOption ? (
                          <div className="AlignedRow" style={{ gap: 10 }}>
                            <CustomTextField
                              value={"Other..."}
                              placeholder={"Other..."}
                              inputMode="text"
                              borderRadius={12}
                              onChange={() => {}}
                              eventTheme={themeType}
                              disabled
                              multiline
                            />
                            <div>
                              <Icon
                                icon={"ion:trash"}
                                height={18}
                                className="TrashButton"
                                onClick={toggleHasOtherOption}
                              />
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    <div className="AlignedRow" style={{ gap: 10, flex: 1 }}>
                      <div style={{ flex: 1 }}>
                        <RectangleButton
                          buttonLabel="Add Option"
                          iconRight={
                            <Icon
                              icon="ion:add-circle"
                              height={18}
                              style={{ color: theme?.PrimaryText.color }}
                            />
                          }
                          onPress={() => {
                            changeInput({
                              options: (
                                newFormQuestion.formInput as
                                  | FormCheckBoxField
                                  | FormRadioField
                              ).options.concat(""),
                            });
                          }}
                          altColor={Colors.TRANSPARENT}
                          altTextColor={
                            theme ? theme.PrimaryText.color : Colors.BLACK
                          }
                          containerStyles={{
                            paddingBlock: 14,
                            paddingInline: 30,
                            border: `1px solid ${
                              theme
                                ? theme.EditableTextFieldBorder.borderColor
                                : Colors.GRAY2
                            }`,
                          }}
                        />
                      </div>
                      {!newFormQuestion.formInput.hasOtherOption ? (
                        <div
                          className="AlignedRowSelect"
                          style={{ flexWrap: "nowrap", minWidth: 0, gap: 4 }}
                          onClick={toggleHasOtherOption}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = "0.7";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = "1";
                          }}
                        >
                          <span
                            className="bodyMedium"
                            style={{ ...theme?.PrimaryText }}
                          >
                            or
                          </span>
                          <span
                            className="bodyMedium"
                            style={{ color: Colors.BLUE5 }}
                          >
                            &quot;Other&quot; Option
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : newFormQuestion.formInput.inputType ===
                FormInputType.TEXT_INPUT ? (
                <div className="ColumnNormal" style={{ gap: 20 }}>
                  {renderQuestionNameSection}
                  <div className="ColumnNormal" style={{ gap: 7 }}>
                    <div className="AlignedRowSpaced">
                      <span className="bodyMedium" style={theme?.PrimaryText}>
                        Placeholder Answer
                      </span>
                      <span className="smallBodySubtext">
                        {newFormQuestion.formInput.placeholder?.length || 0}
                        /1000
                      </span>
                    </div>
                    <CustomTextFieldMultiline
                      value={newFormQuestion.formInput.placeholder || ""}
                      placeholder={"Placeholder (optional)..."}
                      onChange={(e) => {
                        changeInput({ placeholder: e.target.value });
                      }}
                      maxLength={500}
                      containerStyles={{
                        height: 60,
                        backgroundColor: Colors.TRANSPARENT,
                      }}
                      themeType={themeType}
                    />
                  </div>
                </div>
              ) : newFormQuestion.formInput.inputType ===
                FormInputType.FILE_UPLOAD ? (
                <div className="ColumnNormal" style={{ gap: 20 }}>
                  {renderQuestionNameSection}
                  <div className="ColumnNormal" style={{ gap: 7 }}>
                    {previewLabel}
                    <MediaUploadFile
                      supportedFileTypes={["PNG", "JPG", "JPEG", "PDF"]}
                      onUpload={() => {}}
                      theme={theme ?? LightTheme}
                      disabled
                      containerStyles={{
                        opacity: 0.4,
                        cursor: "auto",
                      }}
                    />
                  </div>
                </div>
              ) : newFormQuestion.formInput.inputType ===
                FormInputType.EMAIL ? (
                <div className="ColumnNormal" style={{ gap: 14 }}>
                  {previewLabel}
                  <div
                    className="ColumnNormal"
                    style={{
                      padding: 14,
                      borderRadius: 8,
                      gap: 10,
                      backgroundColor:
                        !theme || (theme && theme === LightTheme)
                          ? Colors.WHITE1
                          : Colors.GRAY7,
                    }}
                  >
                    <h4
                      style={{
                        ...theme?.PrimaryText,
                        fontWeight: "500",
                        paddingRight: 80,
                        fontSize: 15,
                      }}
                    >
                      {newFormQuestion.formInput.inputType ===
                      FormInputType.EMAIL
                        ? "Enter a valid email*"
                        : "I agree to share my phone number with the host"}
                    </h4>
                    {newFormQuestion.formInput.inputType ===
                    FormInputType.EMAIL ? (
                      <div
                        style={{
                          borderRadius: 8,
                          padding: 10,
                          border: "0.5px solid #B9B9B9",
                        }}
                      >
                        <span style={{ color: Colors.GRAY1 }}>
                          Enter email here...
                        </span>
                      </div>
                    ) : (
                      <div className="AlignedRow">
                        <Checkbox
                          checked={true}
                          inputProps={{
                            color: theme?.PrimaryText.color,
                          }}
                          style={{ paddingInline: 5, paddingBlock: 0 }}
                          color="default"
                          sx={{ color: theme?.PrimaryText.color }}
                          checkedIcon={<Icon icon={checkbox} height={24} />}
                          disabled={true}
                        />
                        <span style={theme?.PrimaryText}>I agree</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
              <SwitchToggleItem
                title={"Required Question"}
                description=""
                toggleValue={newFormQuestion.formInput.isRequired}
                onChange={changeIsRequired}
                theme={theme}
              />
              {loggedIn === LoginState.LOGGED_IN ? (
                <div className="AlignedRowSpaced">
                  <div className="ColumnNormal" style={{ gap: 4 }}>
                    <span
                      className="bodyMedium"
                      style={{ ...theme?.PrimaryText }}
                    >
                      Essential Question
                    </span>
                    <span className="smallBodySubtext">
                      This data will be pinned with each unique profile
                    </span>
                  </div>
                  <CustomCheckbox
                    checked={newFormQuestion.isEssential}
                    onChange={essentialCheckmarkOnPress}
                    sx={{ padding: 0 }}
                    altColor={Colors.BLUE5}
                  />
                </div>
              ) : null}
              {formQuestionItem &&
              newInactiveOptions &&
              newInactiveOptions.length > 0 ? (
                <StandardSectionContainer
                  title="Inactive Answers"
                  subtext={"View past deleted answer options"}
                  theme={theme}
                  icon={
                    <Icon
                      icon={"ion:chevron-forward"}
                      height={18}
                      style={{ color: Colors.GRAY1 }}
                    />
                  }
                  onPress={() => setInactiveAnswersModal(true)}
                />
              ) : null}
            </div>
          }
          fixedBottomContent={
            <div className="ColumnNormal">
              <HorizontalDivider altMargin={14} />
              <div
                className="AlignedRow"
                style={{ gap: 10, paddingInline: 14 }}
              >
                <RectangleButton
                  buttonLabel={"Cancel"}
                  onPress={resetOnPress}
                  altColor={
                    theme ? theme.NewPrimaryBG.backgroundColor : Colors.WHITE1
                  }
                  altTextColor={theme ? theme.PrimaryText.color : Colors.BLACK}
                  containerStyles={{
                    paddingBlock: 14,
                    paddingInline: 12,
                    border: `0.5px solid ${
                      theme
                        ? theme.EditableTextFieldBorder.borderColor
                        : Colors.GRAY2
                    }`,
                  }}
                />
                <RectangleButton
                  buttonLabel={
                    formQuestionItem !== undefined ? "Save" : "Create"
                  }
                  onPress={saveNewFormInput}
                  altColor={
                    !newFormQuestion
                      ? Colors.GRAY1
                      : theme === LightTheme || !theme
                        ? Colors.BLACK
                        : Colors.WHITE
                  }
                  altTextColor={
                    theme === LightTheme || !theme ? Colors.WHITE : Colors.BLACK
                  }
                  containerStyles={{ paddingBlock: 14, paddingInline: 12 }}
                />
              </div>
            </div>
          }
          theme={theme}
          closeModal={resetOnPress}
        />
      ) : (
        <PopupModalContainer
          headerComp={
            <div className="ColumnNormal" style={{ gap: 14 }}>
              <div style={{ alignSelf: "flex-start" }}>
                <ContainerButton
                  onPress={() => setInactiveAnswersModal(false)}
                  theme={theme ?? LightTheme}
                  iconName={"ion:chevron-back"}
                  iconSize={18}
                  containerStyles={{ width: 31, height: 31, padding: 0 }}
                />
              </div>
              <div className="ColumnNormal" style={{ gap: 7 }}>
                <span className="sectionTitle">Inactive Answers</span>
                <span className="smallBodySubtext" style={{ fontWeight: 400 }}>
                  View your inactive answer options for this question and change
                  any back to active.
                </span>
              </div>
            </div>
          }
          noExit
          valueComp={
            <div className="ColumnNormal" style={{ gap: 7, paddingBlock: 7 }}>
              {newInactiveOptions
                ? newInactiveOptions.map((option, index) => {
                    return (
                      <div
                        key={index}
                        className="AlignedRow"
                        style={{ gap: 10 }}
                      >
                        <CustomTextField
                          value={option}
                          placeholder={"Answer..."}
                          inputMode="text"
                          borderRadius={12}
                          onChange={(e) => {}}
                          disabled
                          maxLength={1000}
                          multiline
                          minRows={1}
                          maxRows={3}
                          eventTheme={themeType}
                        />
                        <CustomCheckbox
                          checked={selectedInactiveAnswers.includes(option)}
                          onChange={() =>
                            setSelectedInactiveAnswers(
                              selectedInactiveAnswers.includes(option)
                                ? selectedInactiveAnswers.filter(
                                    (o) => o !== option
                                  )
                                : [...selectedInactiveAnswers, option]
                            )
                          }
                          sx={{ padding: 0 }}
                          altColor={Colors.BLUE5}
                        />
                      </div>
                    );
                  })
                : null}
            </div>
          }
          fixedBottomContent={
            <div className="ColumnNormal">
              <HorizontalDivider altMargin={14} />
              <div
                className="AlignedRow"
                style={{ gap: 10, paddingInline: 14 }}
              >
                <RectangleButton
                  buttonLabel={"Cancel"}
                  onPress={() => setInactiveAnswersModal(false)}
                  altColor={
                    theme ? theme.NewPrimaryBG.backgroundColor : Colors.WHITE1
                  }
                  altTextColor={theme ? theme.PrimaryText.color : Colors.BLACK}
                  containerStyles={{
                    paddingBlock: 14,
                    paddingInline: 12,
                    border: `0.5px solid ${
                      theme
                        ? theme.EditableTextFieldBorder.borderColor
                        : Colors.GRAY2
                    }`,
                  }}
                />
                <RectangleButton
                  buttonLabel={"Add"}
                  onPress={activateInputOptions}
                  altColor={
                    theme === LightTheme || !theme ? Colors.BLACK : Colors.WHITE
                  }
                  altTextColor={
                    theme === LightTheme || !theme ? Colors.WHITE : Colors.BLACK
                  }
                  containerStyles={{ paddingBlock: 14, paddingInline: 12 }}
                />
              </div>
            </div>
          }
          theme={theme}
          closeModal={() => setInactiveAnswersModal(false)}
        />
      )}
      {essentialModalVisible ? (
        <div
          style={{
            position: "fixed",
            top: 220,
            left: 80,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            className={"modalContent"}
            style={{
              ...theme?.NewPrimaryBG,
              paddingInline: 14,
              width: "22vw",
              gap: 5,
            }}
          >
            <span style={{ ...theme?.PrimaryText, fontWeight: 500 }}>
              {!newFormQuestion.isEssential
                ? "Make Essential Question?"
                : "Remove Essential Question?"}
            </span>
            <div style={{ color: "#929292", fontSize: 12 }}>
              {!newFormQuestion.isEssential
                ? "You can only have one essential question. Undo existing and set this one as essential?"
                : "This change will immediately take effect."}
            </div>
            <div className="AlignedRow" style={{ gap: 5, marginTop: 5 }}>
              <RectangleButton
                buttonLabel="Cancel"
                onPress={() => setEssentialModalVisible(false)}
                altColor={Colors.WHITE1}
                altTextColor={Colors.BLACK}
                containerStyles={{
                  border: `0.5px solid ${
                    theme
                      ? theme.EditableTextFieldBorder.borderColor
                      : Colors.GRAY2
                  }`,
                }}
              />
              <RectangleButton
                buttonLabel="Yes"
                onPress={() =>
                  changeEssentialQuestionOnPress(!newFormQuestion.isEssential)
                }
                altColor={
                  theme === LightTheme || !theme ? Colors.BLACK : Colors.WHITE
                }
                altTextColor={
                  theme === LightTheme || !theme ? Colors.WHITE : Colors.BLACK
                }
              />
            </div>
          </div>
        </div>
      ) : null}
      <AlertContainer
        headerComp={alertText}
        theme={theme}
        closeModal={() => setAlertText("")}
        hideModal={alertText === ""}
      />
      <ConfirmActionModal
        heading="You are deleting an existing answer option."
        subtext="Any deleted answer options will be moved to inactive. You will still be able to select and segment by data collected using these options. They will no longer be visible to users when collecting data. Confirm to save."
        confirmButtonText="Confirm"
        hideModal={!confirmEditsModal}
        setIsVisible={setConfirmEditsModal}
        confirmOnPress={finalizeFormQuestion}
        theme={theme}
      />
      <ConfirmActionModal
        heading="You are about to change the label of this saved question."
        subtext="This will rename all past data collected with this question. Are you sure you want to continue?"
        confirmButtonText="Yes"
        hideModal={!changeLabelConfirm}
        setIsVisible={setChangeLabelConfirm}
        confirmOnPress={finalizeFormQuestion}
        theme={theme}
      />
    </>
  );
};

export default FormBuilder;
